import type { CompanyEntity } from "@/lib/types/company";
import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import { ENHANCED_COMPANY_DETAIL_CONFIG } from "./CompanyDetailConfig";
import {
  COMPANY_QUICK_FILTERS,
  CREATE_COMPANY_FIELD_ORDER,
  CREATE_COMPANY_FORM_SECTIONS,
  companyBasicFields,
  companyContactCommercialFields,
  companyCustomFilterHooks,
  companyExtendedFields,
  companyLocationFields,
  companyMetadataFields,
  companySavedViews,
} from "../config";

export { COMPANY_STATUS_OPTIONS, COMPANY_QUICK_FILTERS } from "../config";

export const ENHANCED_COMPANIES_CONFIG: SimpleTableConfig<CompanyEntity> = {
  entity: "companies",
  displayName: "Companies",
  statusFieldId: "status",
  defaultPageSize: 10,
  boardViewPageSize: 100,
  defaultSort: { field: "created_at", direction: "desc" },
  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,

  createFormFieldOrder: CREATE_COMPANY_FIELD_ORDER,
  createFormSections: CREATE_COMPANY_FORM_SECTIONS,

  /** Default table column order: account → status → segment → pipeline → recency → web */
  tableColumnOrder: [
    "name",
    "status",
    "industry",
    "active_deal_count",
    "total_deal_value",
    "last_contacted_at",
    "website",
  ],

  /** Card view: account → segment → footprint */
  cardFaceFieldOrder: ["name", "status", "last_contacted_at", "has_active_deals", "industry", "website", "tags"],

  /**
   * Mobile detail modal: sectioned field groups (see `mobileDetailSectionId` on each field).
   * Any entity can opt in by setting this + per-field `mobileDetailSectionId` on `SimpleTableConfig`.
   */
  mobileDetailSections: [
    { id: "basic", label: "Basic Info" },
    { id: "contact", label: "Contact" },
    { id: "location", label: "Location" },
    { id: "commercial", label: "Commercial" },
    { id: "metadata", label: "Metadata" },
  ],

  fields: {
    ...companyBasicFields,
    ...companyContactCommercialFields,
    ...companyLocationFields,
    ...companyMetadataFields,
    ...companyExtendedFields,
  },

  actions: {
    view: true,
    edit: true,
    delete: true,
  },

  // Add rich contextual actions for table rows - reuse from detail config (like contacts)
  detailActions: ENHANCED_COMPANY_DETAIL_CONFIG.actions,

  bulkActions: [
    {
      id: "bulk_export_emails",
      label: "Export Contact Emails",
      icon: "mail",
    },
  ],

  features: {
    search: true,
    filters: true,
    export: true,
    bulkActions: true,
    columnResizing: true,
    columnVisibility: true,
    savedViews: true,
    infiniteScroll: true,
    multiColumnSort: true,
    calendarView: true,
  },

  savedViews: companySavedViews,
  quickFilters: COMPANY_QUICK_FILTERS,
  customFilterHooks: companyCustomFilterHooks,
};
